import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { LoginPrompt } from '../../components/LoginPrompt';
import { ProductDetail, formatProductPrice } from './types';
import { PHONE_COUNTRY_OPTIONS } from '../booking/countryCodes';

interface CustomerDetails {
  name: string;
  email: string;
  phoneCountryCode: string;
  phone: string;
  notes: string;
}

const stepsFor = (type: ProductDetail['product_type']) =>
  type === 'scheduled_seats'
    ? ['Choose Class', 'Your Details', 'Payment', 'Confirmation']
    : ['Your Details', 'Payment', 'Confirmation'];

const getStorageKey = (ref: string) => `cwy-store-${ref}`;

const buildFullPhone = (code: string, digits: string) => {
  const c = code.startsWith('+') ? code : `+${code}`;
  const d = digits.replace(/\D/g, '').slice(0, 10);
  return d ? `${c}${d}` : c;
};

const generateReference = () =>
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `ref-${Date.now()}-${Math.floor(Math.random() * 100000)}`;

export const ProductPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, client } = useAuth();

  const [product, setProduct] = useState<ProductDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutStarted, setCheckoutStarted] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [selectedOfferingId, setSelectedOfferingId] = useState('');
  const [customer, setCustomer] = useState<CustomerDetails>({
    name: '',
    email: '',
    phoneCountryCode: '+1',
    phone: '',
    notes: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const handledSessionRef = useRef<string | null>(null);

  const stepLabels = useMemo(() => (product ? stepsFor(product.product_type) : []), [product]);

  const selectedOffering = useMemo(
    () => product?.offerings?.find((o) => o.id === selectedOfferingId) ?? null,
    [product, selectedOfferingId]
  );

  const pricing = useMemo(() => {
    if (product?.product_type === 'scheduled_seats') {
      if (selectedOffering) {
        return {
          price: selectedOffering.price,
          tax_amount: selectedOffering.tax_amount,
          total_amount: selectedOffering.total_amount,
          currency: selectedOffering.currency,
        };
      }
    }
    if (product?.product_type === 'flat_fee') {
      return {
        price: product.price || 0,
        tax_amount: product.tax_amount || 0,
        total_amount: product.total_amount || 0,
        currency: product.currency,
      };
    }
    return null;
  }, [product, selectedOffering, selectedOfferingId]);

  useEffect(() => {
    if (!slug) return;
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetch(API_ENDPOINTS.PRODUCT_BY_SLUG(slug));
        if (!response.ok) throw new Error('Product not found');
        const data = await response.json();
        setProduct(data.product);
      } catch (err) {
        console.error(err);
        setError('Product not found or unavailable.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug]);

  useEffect(() => {
    if (client && checkoutStarted) {
      setCustomer((prev) => ({
        ...prev,
        name: prev.name || client.name || '',
        email: prev.email || client.email || '',
        phone: prev.phone || client.phone?.replace(/\D/g, '').slice(-10) || '',
      }));
    }
  }, [client, checkoutStarted]);

  const finalizeRegistration = useCallback(
    async (ref: string, sessionId: string) => {
      setIsSubmitting(true);
      setPaymentMessage('Confirming your payment...');
      setCheckoutStarted(true);
      try {
        const statusRes = await fetch(API_ENDPOINTS.STRIPE_STATUS(sessionId));
        const statusData = await statusRes.json();

        if (
          !statusRes.ok ||
          !['confirmed', 'verified', 'pending'].includes(statusData.transaction?.status)
        ) {
          throw new Error('Payment not confirmed yet');
        }

        const confirmRes = await fetch(API_ENDPOINTS.PRODUCT_CONFIRM, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ registrationReference: ref, sessionId }),
        });
        const confirmData = await confirmRes.json();

        if (!confirmRes.ok || confirmData.status !== 'confirmed') {
          throw new Error(confirmData.error || 'Registration could not be confirmed');
        }

        setConfirmed(true);
        setPaymentMessage('Payment successful! You are registered.');
        setActiveStep(stepLabels.length - 1);
        localStorage.removeItem(getStorageKey(ref));
        window.history.replaceState(null, '', `/store/${slug}`);
      } catch (err) {
        console.error(err);
        setError(err instanceof Error ? err.message : 'Confirmation failed');
        setPaymentMessage('');
      } finally {
        setIsSubmitting(false);
      }
    },
    [slug, stepLabels.length]
  );

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    const ref = params.get('ref');
    const cancelled = params.get('payment') === 'cancelled';

    if (cancelled) {
      setError('Payment was cancelled. You can try again.');
      setCheckoutStarted(true);
      setActiveStep(product?.product_type === 'scheduled_seats' ? 2 : 1);
      window.history.replaceState(null, '', `/store/${slug}`);
      return;
    }

    if (sessionId && ref && handledSessionRef.current !== sessionId) {
      handledSessionRef.current = sessionId;
      setCheckoutStarted(true);
      finalizeRegistration(ref, sessionId);
    }
  }, [location.search, finalizeRegistration, slug, product?.product_type]);

  const handleBuyNow = () => {
    if (!isAuthenticated) {
      navigate(`/login?redirect=${encodeURIComponent(`/store/${slug}`)}`);
      return;
    }
    setCheckoutStarted(true);
    setActiveStep(0);
  };

  const validateCustomer = () => {
    if (!customer.name.trim()) return 'Name is required';
    if (!customer.email.trim() || !customer.email.includes('@')) return 'Valid email is required';
    const digits = customer.phone.replace(/\D/g, '');
    if (digits.length !== 10) return 'Phone must be 10 digits';
    return null;
  };

  const handleNext = () => {
    setError(null);
    if (product?.product_type === 'scheduled_seats' && activeStep === 0) {
      if (!selectedOfferingId) {
        setError('Please select a class date');
        return;
      }
    }
    const detailsStep = product?.product_type === 'scheduled_seats' ? 1 : 0;
    if (activeStep === detailsStep) {
      const validationError = validateCustomer();
      if (validationError) {
        setError(validationError);
        return;
      }
    }
    setActiveStep((s) => s + 1);
  };

  const handlePayment = async () => {
    if (!product || !pricing || isSubmitting) return;
    const validationError = validateCustomer();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      setPaymentMessage('Redirecting to secure checkout...');

      const registrationReference = generateReference();
      const phone = buildFullPhone(customer.phoneCountryCode, customer.phone);

      localStorage.setItem(
        getStorageKey(registrationReference),
        JSON.stringify({ productSlug: product.slug, offeringId: selectedOfferingId, customer })
      );

      const successUrl = `${window.location.origin}/store/${product.slug}?ref=${encodeURIComponent(registrationReference)}&session_id={CHECKOUT_SESSION_ID}`;
      const cancelUrl = `${window.location.origin}/store/${product.slug}?ref=${encodeURIComponent(registrationReference)}&payment=cancelled`;

      const response = await fetch(API_ENDPOINTS.PRODUCT_CHECKOUT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productSlug: product.slug,
          offeringId: product.product_type === 'scheduled_seats' ? selectedOfferingId : undefined,
          customerName: customer.name.trim(),
          customerEmail: customer.email.trim(),
          customerPhone: phone,
          phoneCountryCode: customer.phoneCountryCode,
          notes: customer.notes.trim(),
          successUrl,
          cancelUrl,
          registrationReference,
        }),
      });

      const data = await response.json();
      if (!response.ok || !data.sessionUrl) {
        throw new Error(data.error || 'Unable to start payment');
      }

      window.location.href = data.sessionUrl;
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : 'Payment failed');
      setPaymentMessage('');
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!product) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Alert severity="error">{error || 'Product not found'}</Alert>
        <Button sx={{ mt: 2 }} onClick={() => navigate('/')}>
          Back to home
        </Button>
      </Container>
    );
  }

  const detailsStepIndex = product.product_type === 'scheduled_seats' ? 1 : 0;
  const paymentStepIndex = product.product_type === 'scheduled_seats' ? 2 : 1;
  const confirmStepIndex = stepLabels.length - 1;
  const openClasses = product.offerings?.filter((o) => !o.is_full && o.available_seats > 0).length;

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Button onClick={() => navigate('/')} sx={{ mb: 2, textTransform: 'none' }}>
        ← Back to marketplace
      </Button>

      {!checkoutStarted ? (
        <Paper elevation={0} sx={{ p: { xs: 3, md: 4 }, borderRadius: 2, border: '1px solid #e8eaed' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {product.name}
          </Typography>
          {product.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              {product.description}
            </Typography>
          )}

          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 3 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#B12704' }}>
              {formatProductPrice(product)} + tax
            </Typography>
            {product.product_type === 'scheduled_seats' && openClasses !== undefined && (
              <Typography variant="body2" sx={{ color: '#007600' }}>
                {openClasses} upcoming class{openClasses === 1 ? '' : 'es'} available
              </Typography>
            )}
          </Box>

          {product.product_type === 'scheduled_seats' && product.offerings && product.offerings.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
                Upcoming sessions
              </Typography>
              {product.offerings.map((offering) => (
                <Paper
                  key={offering.id}
                  variant="outlined"
                  sx={{ p: 2, mb: 1, opacity: offering.is_full ? 0.5 : 1 }}
                >
                  <Typography fontWeight={500}>
                    {format(new Date(offering.session_date), 'EEEE, MMMM d, yyyy')}
                  </Typography>
                  {offering.location && (
                    <Typography variant="body2" color="text.secondary">
                      {offering.location}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {offering.currency} ${offering.total_amount.toFixed(2)} incl. tax ·{' '}
                    {offering.is_full ? 'Full' : `${offering.available_seats} seats left`}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}

          <Button
            variant="contained"
            size="large"
            onClick={handleBuyNow}
            sx={{
              bgcolor: '#FFD814',
              color: '#0f0f0f',
              fontWeight: 600,
              px: 4,
              '&:hover': { bgcolor: '#F7CA00' },
            }}
          >
            {isAuthenticated ? 'Buy now' : 'Sign in to buy'}
          </Button>

          {!isAuthenticated && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Browse freely — sign in when you&apos;re ready to order and pay securely via Stripe.
            </Typography>
          )}
        </Paper>
      ) : !isAuthenticated ? (
        <LoginPrompt
          title="Sign in to complete your order"
          message="Please sign in or create an account to purchase this product and proceed to payment."
          redirectPath={`/store/${slug}`}
        />
      ) : (
        <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, borderRadius: 2, border: '1px solid #e8eaed', maxWidth: 720, mx: 'auto' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 2 }}>
            Checkout — {product.name}
          </Typography>

          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {stepLabels.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}
          {paymentMessage && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {paymentMessage}
            </Alert>
          )}

          {product.product_type === 'scheduled_seats' && activeStep === 0 && (
            <Box>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                Select a class date
              </Typography>
              {!product.offerings?.length && (
                <Typography color="text.secondary">No upcoming classes scheduled.</Typography>
              )}
              {product.offerings?.map((offering) => (
                <Paper
                  key={offering.id}
                  variant="outlined"
                  onClick={() => !offering.is_full && setSelectedOfferingId(offering.id)}
                  sx={{
                    p: 2,
                    mb: 1.5,
                    cursor: offering.is_full ? 'not-allowed' : 'pointer',
                    borderColor: selectedOfferingId === offering.id ? '#232f3e' : undefined,
                    borderWidth: selectedOfferingId === offering.id ? 2 : 1,
                    opacity: offering.is_full ? 0.5 : 1,
                  }}
                >
                  <Typography fontWeight={500}>
                    {format(new Date(offering.session_date), 'EEEE, MMMM d, yyyy')}
                  </Typography>
                  {offering.location && (
                    <Typography variant="body2" color="text.secondary">
                      {offering.location}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {offering.currency} ${offering.total_amount.toFixed(2)} incl. tax ·{' '}
                    {offering.is_full ? 'Full' : `${offering.available_seats} seats left`}
                  </Typography>
                </Paper>
              ))}
            </Box>
          )}

          {activeStep === detailsStepIndex && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {pricing && (
                <Alert severity="success">
                  Total: {pricing.currency} ${pricing.total_amount.toFixed(2)} (includes $
                  {pricing.tax_amount.toFixed(2)} tax)
                  {selectedOffering && (
                    <> · Class on {format(new Date(selectedOffering.session_date), 'MMM d, yyyy')}</>
                  )}
                </Alert>
              )}
              <TextField
                label="Full name"
                required
                fullWidth
                value={customer.name}
                onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
              />
              <TextField
                label="Email"
                required
                fullWidth
                type="email"
                value={customer.email}
                onChange={(e) => setCustomer({ ...customer, email: e.target.value })}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FormControl sx={{ minWidth: 100 }}>
                  <TextField
                    select
                    label="Code"
                    value={customer.phoneCountryCode}
                    onChange={(e) =>
                      setCustomer({ ...customer, phoneCountryCode: e.target.value })
                    }
                  >
                    {PHONE_COUNTRY_OPTIONS.map((c) => (
                      <MenuItem key={c.code} value={c.code}>
                        {c.code} {c.name}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormControl>
                <TextField
                  label="Phone (10 digits)"
                  required
                  fullWidth
                  value={customer.phone}
                  onChange={(e) =>
                    setCustomer({
                      ...customer,
                      phone: e.target.value.replace(/\D/g, '').slice(0, 10),
                    })
                  }
                />
              </Box>
              <TextField
                label="Notes (optional)"
                fullWidth
                multiline
                rows={2}
                value={customer.notes}
                onChange={(e) => setCustomer({ ...customer, notes: e.target.value })}
              />
            </Box>
          )}

          {activeStep === paymentStepIndex && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              {pricing && (
                <>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    {pricing.currency} ${pricing.total_amount.toFixed(2)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    You will be redirected to Stripe to pay securely.
                  </Typography>
                </>
              )}
              <Button variant="contained" size="large" onClick={handlePayment} disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={24} /> : 'Pay with Stripe'}
              </Button>
            </Box>
          )}

          {activeStep === confirmStepIndex && confirmed && (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" color="success.main" gutterBottom>
                You&apos;re all set!
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Thank you, {customer.name}.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                A confirmation will be sent to {customer.email}.
                {selectedOffering && (
                  <> Your class is on {format(new Date(selectedOffering.session_date), 'MMMM d, yyyy')}.</>
                )}
              </Typography>
              <Button sx={{ mt: 3 }} onClick={() => navigate('/chat')}>
                Chat with support
              </Button>
            </Box>
          )}

          {!confirmed && (
            <>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button
                  disabled={activeStep === 0 || isSubmitting}
                  onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                >
                  Back
                </Button>
                {activeStep < paymentStepIndex && (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                    sx={{ bgcolor: '#FFD814', color: '#0f0f0f', '&:hover': { bgcolor: '#F7CA00' } }}
                  >
                    Continue
                  </Button>
                )}
              </Box>
            </>
          )}
        </Paper>
      )}
    </Container>
  );
};
